'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function RoofingCalculator() {
  const t = useTranslations("tools.roofingCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [currentTab, setCurrentTab] = useState<'pitch' | 'angle'>('pitch');
  const [areaInputs, setAreaInputs] = useState({
    houseArea: 2000,
    areaUnit: 'feet',
    roofPitch: 6,
    roofAngle: 26.6,
    eaves: 1,
    eavesUnit: 'feet',
    price: 0,
    priceUnit: 'feet'
  });

  const [materialInputs, setMaterialInputs] = useState({
    roofArea: 2236,
    roofAreaUnit: 'feet'
  });

  const [areaResults, setAreaResults] = useState<any>(null);
  const [materialResults, setMaterialResults] = useState<any>(null);

  // Pitch multipliers and angles
  const pitchMultipliers: { [key: number]: number } = {
    1: 1.003, 2: 1.014, 3: 1.031, 4: 1.054, 5: 1.083, 6: 1.118,
    7: 1.158, 8: 1.202, 9: 1.250, 10: 1.302, 11: 1.357, 12: 1.414,
    13: 1.474, 14: 1.537, 15: 1.601, 16: 1.667, 17: 1.734, 18: 1.803,
    19: 1.873, 20: 1.944, 21: 2.016, 22: 2.088, 23: 2.162, 24: 2.236
  };

  const pitchAngles: { [key: number]: number } = {
    1: 4.8, 2: 9.5, 3: 14.0, 4: 18.4, 5: 22.6, 6: 26.6,
    7: 30.3, 8: 33.7, 9: 36.9, 10: 39.8, 11: 42.5, 12: 45.0,
    13: 47.3, 14: 49.4, 15: 51.3, 16: 53.1, 17: 54.8, 18: 56.3,
    19: 57.7, 20: 59.0, 21: 60.3, 22: 61.4, 23: 62.4, 24: 63.4
  };

  const conversionToFeet: { [key: string]: number } = {
    feet: 1,
    meters: 10.7639,
    yards: 9,
    inches: 0.00694444,
    centimeters: 0.00107639
  };

  const conversionToFeetLinear: { [key: string]: number } = {
    feet: 1,
    meters: 3.28084,
    yards: 3,
    inches: 0.0833333,
    centimeters: 0.0328084
  };

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const handleAreaCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    let multiplier: number;
    let angle: number;
    let pitch: number;

    if (currentTab === 'pitch') {
      pitch = areaInputs.roofPitch;
      multiplier = pitchMultipliers[pitch];
      angle = pitchAngles[pitch];
    } else {
      angle = areaInputs.roofAngle;
      multiplier = 1 / Math.cos(angle * Math.PI / 180);
      pitch = Math.round((Math.tan(angle * Math.PI / 180) * 12) * 10) / 10;
    }

    const houseAreaInFeet = areaInputs.houseArea * conversionToFeet[areaInputs.areaUnit];
    const eavesInFeet = areaInputs.eaves * conversionToFeetLinear[areaInputs.eavesUnit];
    const perimeter = 4 * Math.sqrt(houseAreaInFeet);
    const eavesArea = perimeter * eavesInFeet * multiplier;

    const totalRoofAreaFeet = (houseAreaInFeet * multiplier) + eavesArea;
    const totalRoofArea = totalRoofAreaFeet / conversionToFeet[areaInputs.areaUnit];

    let totalCost = 0;
    if (areaInputs.price > 0) {
      const pricePerFoot = areaInputs.price / conversionToFeet[areaInputs.priceUnit];
      totalCost = totalRoofAreaFeet * pricePerFoot;
    }

    setAreaResults({
      totalRoofArea: totalRoofArea.toFixed(2),
      unit: areaInputs.areaUnit,
      pitch: currentTab === 'pitch' ? pitch : pitch.toFixed(1),
      angle: angle.toFixed(1),
      totalCost: totalCost > 0 ? totalCost.toFixed(2) : null
    });

    setMaterialInputs({
      roofArea: parseFloat(totalRoofAreaFeet.toFixed(2)),
      roofAreaUnit: 'feet'
    });
  };

  const handleMaterialCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const roofAreaInFeet = materialInputs.roofArea * conversionToFeet[materialInputs.roofAreaUnit];

    const roofingSquares = roofAreaInFeet / 100;
    const shingleBundles = Math.ceil(roofingSquares * 3);
    const underlaymentRolls = Math.ceil((roofAreaInFeet * 1.15) / 400);
    const ridgeBundles = Math.ceil(roofingSquares * 0.42);
    const starterBundles = Math.ceil(roofingSquares * 0.6);

    setMaterialResults({
      roofingSquares: roofingSquares.toFixed(2),
      shingleBundles,
      underlaymentRolls,
      ridgeBundles,
      starterBundles
    });
  };

  const clearAreaForm = () => {
    setAreaInputs({
      houseArea: 2000,
      areaUnit: 'feet',
      roofPitch: 6,
      roofAngle: 26.6,
      eaves: 1,
      eavesUnit: 'feet',
      price: 0,
      priceUnit: 'feet'
    });
    setAreaResults(null);
  };

  const clearMaterialForm = () => {
    setMaterialInputs({
      roofArea: 2236,
      roofAreaUnit: 'feet'
    });
    setMaterialResults(null);
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const calculatorGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '2rem', marginBottom: '2rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' }
  );

  const calculatorCardStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const cardTitleStyle = getResponsiveStyle(
    {
      color: colors.textPrimary,
      marginBottom: '1.5rem',
      fontWeight: 600,
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    { fontSize: '1.25rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.5rem' }
  );

  const tabButtonsStyle = {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1.5rem',
    background: '#f1f5f9',
    padding: '0.25rem',
    borderRadius: '0.5rem'
  };

  const tabBtnStyle = (isActive: boolean) => getResponsiveStyle(
    {
      flex: 1,
      border: 'none',
      background: isActive ? 'white' : 'transparent',
      color: isActive ? '#2563eb' : '#64748b',
      fontWeight: 500,
      cursor: 'pointer',
      borderRadius: '0.375rem',
      transition: 'all 0.3s ease',
      boxShadow: isActive ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none'
    },
    { padding: '0.625rem 0.75rem', fontSize: '0.875rem' },
    { padding: '0.75rem 1rem', fontSize: '1rem' },
    { padding: '0.75rem 1rem', fontSize: '1rem' }
  );

  const formGroupStyle = {
    marginBottom: '1.5rem'
  };

  const labelStyle = getResponsiveStyle(
    {
      display: 'block',
      marginBottom: '0.5rem',
      color: '#475569',
      fontWeight: 500
    },
    { fontSize: '0.875rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' }
  );

  const inputWrapperStyle = {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center'
  };

  const inputStyle = getResponsiveStyle(
    {
      flex: 1,
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
      background: 'white'
    },
    { padding: '0.625rem 0.875rem', fontSize: '0.875rem' },
    { padding: '0.75rem 1rem', fontSize: '1rem' },
    { padding: '0.75rem 1rem', fontSize: '1rem' }
  );

  const selectStyle = getResponsiveStyle(
    {
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
      background: 'white',
      cursor: 'pointer'
    },
    { padding: '0.625rem 0.875rem', fontSize: '0.875rem' },
    { padding: '0.75rem 1rem', fontSize: '1rem' },
    { padding: '0.75rem 1rem', fontSize: '1rem' }
  );

  const calculateBtnStyle = getResponsiveStyle(
    {
      width: '100%',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginTop: '1rem'
    },
    { padding: '0.875rem 1rem', fontSize: '1rem' },
    { padding: '1rem', fontSize: '1.1rem' },
    { padding: '1rem', fontSize: '1.1rem' }
  );

  const clearBtnStyle = getResponsiveStyle(
    {
      width: '100%',
      background: '#f1f5f9',
      color: '#64748b',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginTop: '0.5rem'
    },
    { padding: '0.625rem', fontSize: '0.875rem' },
    { padding: '0.75rem', fontSize: '1rem' },
    { padding: '0.75rem', fontSize: '1rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      marginTop: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const h3Style = getResponsiveStyle(
    {
      color: colors.textPrimary,
      marginBottom: '1.5rem',
      fontWeight: 600
    },
    { fontSize: '1.25rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.5rem' }
  );

  const resultGridStyle = getResponsiveStyle(
    {
      display: 'grid',
      gap: '1rem'
    },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }
  );

  const resultItemStyle = (isHighlight: boolean = false) => getResponsiveStyle(
    {
      background: isHighlight ? 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)' : 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderRadius: '0.75rem',
      borderLeft: `4px solid ${isHighlight ? '#f59e0b' : '#2563eb'}`
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.25rem' }
  );

  const resultLabelStyle = getResponsiveStyle(
    {
      display: 'block',
      color: '#64748b',
      marginBottom: '0.5rem',
      fontWeight: 500
    },
    { fontSize: '0.875rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.9rem' }
  );

  const resultValueStyle = getResponsiveStyle(
    {
      display: 'block',
      color: colors.textPrimary,
      fontWeight: 700
    },
    { fontSize: '1.25rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.5rem' }
  );

  const materialTableStyle = {
    width: '100%',
    borderCollapse: 'collapse' as const,
    marginTop: '1rem'
  };

  const tableHeaderStyle = getResponsiveStyle(
    {
      padding: '1rem',
      textAlign: 'left' as const,
      borderBottom: '1px solid #e2e8f0',
      background: '#f8fafc',
      color: '#475569',
      fontWeight: 600
    },
    { fontSize: '0.875rem', padding: '0.75rem' },
    { fontSize: '1rem', padding: '1rem' },
    { fontSize: '1rem', padding: '1rem' }
  );

  const tableCellStyle = getResponsiveStyle(
    {
      padding: '1rem',
      textAlign: 'left' as const,
      borderBottom: '1px solid #e2e8f0'
    },
    { fontSize: '0.875rem', padding: '0.75rem' },
    { fontSize: '1rem', padding: '1rem' },
    { fontSize: '1rem', padding: '1rem' }
  );

  const infoBoxStyle = getResponsiveStyle(
    {
      background: '#eff6ff',
      borderLeft: '4px solid #2563eb',
      borderRadius: '0.5rem',
      marginTop: '1rem',
      color: '#475569'
    },
    { padding: '0.875rem', fontSize: '0.875rem' },
    { padding: '1rem', fontSize: '0.9rem' },
    { padding: '1rem', fontSize: '0.9rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    {
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '1rem'
    },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    {
      lineHeight: 1.6,
      color: colors.textSecondary,
      marginBottom: '1rem'
    },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const listStyle = getResponsiveStyle(
    {
      marginLeft: '1.5rem',
      marginBottom: '1rem',
      color: colors.textSecondary
    },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const listItemStyle = {
    marginBottom: '0.75rem',
    lineHeight: 1.6
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t("title")}</h1>
        <p style={summaryTextStyle}>{t("subtitle")}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={calculatorGridStyle}>
          <div style={calculatorCardStyle}>
            <h3 style={cardTitleStyle}>📐 {t("areaCalculatorTitle")}</h3>

            <div style={tabButtonsStyle}>
              <button
                type="button"
                style={tabBtnStyle(currentTab === 'pitch')}
                onClick={() => setCurrentTab('pitch')}
              >
                {t("roofPitchTab")}
              </button>
              <button
                type="button"
                style={tabBtnStyle(currentTab === 'angle')}
                onClick={() => setCurrentTab('angle')}
              >
                {t("roofAngleTab")}
              </button>
            </div>

            <form onSubmit={handleAreaCalculate}>
              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="house-area">{t("houseBaseArea")}</label>
                <div style={inputWrapperStyle}>
                  <input
                    type="number"
                    id="house-area"
                    style={inputStyle}
                    value={areaInputs.houseArea}
                    onChange={(e) => setAreaInputs({...areaInputs, houseArea: parseFloat(e.target.value) || 0})}
                    step="0.01"
                    required
                  />
                  <select
                    id="area-unit"
                    style={selectStyle}
                    value={areaInputs.areaUnit}
                    onChange={(e) => setAreaInputs({...areaInputs, areaUnit: e.target.value})}
                  >
                    <option value="feet">{t("sqFt")}</option>
                    <option value="meters">{t("sqM")}</option>
                    <option value="yards">{t("sqYd")}</option>
                    <option value="inches">{t("sqIn")}</option>
                    <option value="centimeters">{t("sqCm")}</option>
                  </select>
                </div>
              </div>

              {currentTab === 'pitch' && (
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="roof-pitch">{t("roofPitch")}</label>
                  <select
                    id="roof-pitch"
                    style={{...selectStyle, width: '100%'}}
                    value={areaInputs.roofPitch}
                    onChange={(e) => setAreaInputs({...areaInputs, roofPitch: parseInt(e.target.value)})}
                  >
                    {Array.from({length: 24}, (_, i) => i + 1).map(pitch => (
                      <option key={pitch} value={pitch}>{pitch}/12</option>
                    ))}
                  </select>
                </div>
              )}

              {currentTab === 'angle' && (
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="roof-angle">{t("roofAngleDegrees")}</label>
                  <input
                    type="number"
                    id="roof-angle"
                    style={{...inputStyle, width: '100%'}}
                    value={areaInputs.roofAngle}
                    onChange={(e) => setAreaInputs({...areaInputs, roofAngle: parseFloat(e.target.value) || 0})}
                    step="0.1"
                    min="0"
                    max="90"
                  />
                </div>
              )}

              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="eaves">{t("eavesStickOut")}</label>
                <div style={inputWrapperStyle}>
                  <input
                    type="number"
                    id="eaves"
                    style={inputStyle}
                    value={areaInputs.eaves}
                    onChange={(e) => setAreaInputs({...areaInputs, eaves: parseFloat(e.target.value) || 0})}
                    step="0.01"
                    min="0"
                    required
                  />
                  <select
                    id="eaves-unit"
                    style={selectStyle}
                    value={areaInputs.eavesUnit}
                    onChange={(e) => setAreaInputs({...areaInputs, eavesUnit: e.target.value})}
                  >
                    <option value="feet">{t("feet")}</option>
                    <option value="meters">{t("meters")}</option>
                    <option value="yards">{t("yards")}</option>
                    <option value="inches">{t("inches")}</option>
                    <option value="centimeters">{t("cm")}</option>
                  </select>
                </div>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="price">{t("pricePerUnit")}</label>
                <div style={inputWrapperStyle}>
                  <input
                    type="number"
                    id="price"
                    style={inputStyle}
                    value={areaInputs.price || ''}
                    onChange={(e) => setAreaInputs({...areaInputs, price: parseFloat(e.target.value) || 0})}
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                  />
                  <select
                    id="price-unit"
                    style={selectStyle}
                    value={areaInputs.priceUnit}
                    onChange={(e) => setAreaInputs({...areaInputs, priceUnit: e.target.value})}
                  >
                    <option value="feet">{t("perSqFt")}</option>
                    <option value="meters">{t("perSqM")}</option>
                    <option value="yards">{t("perSqYd")}</option>
                    <option value="inches">{t("perSqIn")}</option>
                    <option value="centimeters">{t("perSqCm")}</option>
                  </select>
                </div>
              </div>

              <button type="submit" style={calculateBtnStyle}>{t("calculateRoofArea")}</button>
              <button type="button" style={clearBtnStyle} onClick={clearAreaForm}>{t("clear")}</button>
            </form>
          </div>

          <div style={calculatorCardStyle}>
            <h3 style={cardTitleStyle}>🧱 {t("materialCalculatorTitle")}</h3>

            <form onSubmit={handleMaterialCalculate}>
              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="roof-area">{t("roofArea")}</label>
                <div style={inputWrapperStyle}>
                  <input
                    type="number"
                    id="roof-area"
                    style={inputStyle}
                    value={materialInputs.roofArea}
                    onChange={(e) => setMaterialInputs({...materialInputs, roofArea: parseFloat(e.target.value) || 0})}
                    step="0.01"
                    required
                  />
                  <select
                    id="roof-area-unit"
                    style={selectStyle}
                    value={materialInputs.roofAreaUnit}
                    onChange={(e) => setMaterialInputs({...materialInputs, roofAreaUnit: e.target.value})}
                  >
                    <option value="feet">{t("sqFt")}</option>
                    <option value="meters">{t("sqM")}</option>
                    <option value="yards">{t("sqYd")}</option>
                    <option value="inches">{t("sqIn")}</option>
                    <option value="centimeters">{t("sqCm")}</option>
                  </select>
                </div>
              </div>

              <button type="submit" style={calculateBtnStyle}>{t("calculateMaterials")}</button>
              <button type="button" style={clearBtnStyle} onClick={clearMaterialForm}>{t("clear")}</button>
            </form>

            <div style={infoBoxStyle}>
              <strong>💡 {t("tip")}</strong> {t("tipText")}
            </div>
          </div>
        </div>

        {areaResults && (
          <div style={resultsStyle}>
            <h3 style={h3Style}>📊 {t("roofAreaResults")}</h3>
            <div style={resultGridStyle}>
              <div style={resultItemStyle(true)}>
                <span style={resultLabelStyle}>{t("totalRoofArea")}</span>
                <span style={resultValueStyle}>
                  {areaResults.totalRoofArea} {areaResults.unit === 'feet' ? t("sqFt") :
                    areaResults.unit === 'meters' ? t("sqM") :
                    areaResults.unit === 'yards' ? t("sqYd") :
                    areaResults.unit === 'inches' ? t("sqIn") : t("sqCm")}
                </span>
              </div>
              <div style={resultItemStyle()}>
                <span style={resultLabelStyle}>{t("roofPitch")}</span>
                <span style={resultValueStyle}>{areaResults.pitch}/12</span>
              </div>
              <div style={resultItemStyle()}>
                <span style={resultLabelStyle}>{t("roofAngle")}</span>
                <span style={resultValueStyle}>{areaResults.angle}°</span>
              </div>
              {areaResults.totalCost && (
                <div style={resultItemStyle()}>
                  <span style={resultLabelStyle}>{t("estimatedCost")}</span>
                  <span style={resultValueStyle}>${areaResults.totalCost}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {materialResults && (
          <div style={{...resultsStyle, marginTop: '2rem'}}>
            <h3 style={h3Style}>📦 {t("materialRequirements")}</h3>
            <table style={materialTableStyle}>
              <thead>
                <tr>
                  <th style={tableHeaderStyle}>{t("materialType")}</th>
                  <th style={tableHeaderStyle}>{t("quantityNeeded")}</th>
                  <th style={tableHeaderStyle}>{t("coverage")}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={tableCellStyle}><strong>{t("roofingShingles")}</strong></td>
                  <td style={tableCellStyle}>{materialResults.shingleBundles} {t("bundles")}</td>
                  <td style={tableCellStyle}>{t("shinglesCoverage")}</td>
                </tr>
                <tr>
                  <td style={tableCellStyle}><strong>{t("roofingSquares")}</strong></td>
                  <td style={tableCellStyle}>{materialResults.roofingSquares} {t("squares")}</td>
                  <td style={tableCellStyle}>{t("squaresCoverage")}</td>
                </tr>
                <tr>
                  <td style={tableCellStyle}><strong>{t("underlayment")}</strong></td>
                  <td style={tableCellStyle}>{materialResults.underlaymentRolls} {t("rolls")}</td>
                  <td style={tableCellStyle}>{t("underlaymentCoverage")}</td>
                </tr>
                <tr>
                  <td style={tableCellStyle}><strong>{t("roofRidge")}</strong></td>
                  <td style={tableCellStyle}>{materialResults.ridgeBundles} {t("bundles")}</td>
                  <td style={tableCellStyle}>{t("ridgeCoverage")}</td>
                </tr>
                <tr>
                  <td style={tableCellStyle}><strong>{t("starterShingles")}</strong></td>
                  <td style={tableCellStyle}>{materialResults.starterBundles} {t("bundles")}</td>
                  <td style={tableCellStyle}>{t("starterCoverage")}</td>
                </tr>
              </tbody>
            </table>
            <div style={infoBoxStyle}>
              <strong>📝 {t("note")}</strong> {t("noteText")}
            </div>
          </div>
        )}
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("introTitle")}</h2>
        <p style={paragraphStyle}>{t("introParagraph1")}</p>
        <p style={paragraphStyle}>{t("introParagraph2")}</p>
        <p style={paragraphStyle}>{t("introParagraph3")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("howToUseTitle")}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>{t("howToStep1")}</li>
          <li style={listItemStyle}>{t("howToStep2")}</li>
          <li style={listItemStyle}>{t("howToStep3")}</li>
          <li style={listItemStyle}>{t("howToStep4")}</li>
          <li style={listItemStyle}>{t("howToStep5")}</li>
          <li style={listItemStyle}>{t("howToStep6")}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("insightsTitle")}</h2>
        <p style={paragraphStyle}>{t("insight1")}</p>
        <p style={paragraphStyle}>{t("insight2")}</p>
        <p style={paragraphStyle}>{t("insight3")}</p>
        <p style={paragraphStyle}>{t("insight4")}</p>
        <p style={paragraphStyle}>{t("insight5")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("detailsTitle")}</h2>
        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem'}}>{t("understandingPitch")}</h3>
        <p style={paragraphStyle}>{t("pitchExplanation")}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem'}}>{t("materialEstimates")}</h3>
        <p style={paragraphStyle}>{t("materialsExplanation")}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem'}}>{t("costFactors")}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t("costFactor1")}</li>
          <li style={listItemStyle}>{t("costFactor2")}</li>
          <li style={listItemStyle}>{t("costFactor3")}</li>
          <li style={listItemStyle}>{t("costFactor4")}</li>
          <li style={listItemStyle}>{t("costFactor5")}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("faqTitle")}</h2>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginTop: '1.5rem'}}>{t("faq1Question")}</h3>
        <p style={paragraphStyle}>{t("faq1Answer")}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginTop: '1.5rem'}}>{t("faq2Question")}</h3>
        <p style={paragraphStyle}>{t("faq2Answer")}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginTop: '1.5rem'}}>{t("faq3Question")}</h3>
        <p style={paragraphStyle}>{t("faq3Answer")}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginTop: '1.5rem'}}>{t("faq4Question")}</h3>
        <p style={paragraphStyle}>{t("faq4Answer")}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginTop: '1.5rem'}}>{t("faq5Question")}</h3>
        <p style={paragraphStyle}>{t("faq5Answer")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("referencesTitle")}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>
            <a href="https://fieldcomplete.com/blog/mobile-solutions-for-contractors/what-is-a-roofing-calculator-app-understanding-its-benefits-and-features"
               style={{color: colors.primary, textDecoration: 'none'}}
               target="_blank"
               rel="noopener noreferrer">
              {t("reference1")}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.austinroofingandconstruction.com/top-benefits-of-using-a-roofing-calculator"
               style={{color: colors.primary, textDecoration: 'none'}}
               target="_blank"
               rel="noopener noreferrer">
              {t("reference2")}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.rooferbase.com/blog/whats-the-average-cost-of-a-new-roof-in-2025"
               style={{color: colors.primary, textDecoration: 'none'}}
               target="_blank"
               rel="noopener noreferrer">
              {t("reference3")}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.roofchief.com/resources/roofing-estimating-software-2025-complete-buyers-guide-for-contractors"
               style={{color: colors.primary, textDecoration: 'none'}}
               target="_blank"
               rel="noopener noreferrer">
              {t("reference4")}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.servicetitan.com/blog/roofing-technology"
               style={{color: colors.primary, textDecoration: 'none'}}
               target="_blank"
               rel="noopener noreferrer">
              {t("reference5")}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}